import { strokeRibs } from '../core/paint/stroke.js';
import type { PlacedSticker, Stroke } from '../core/save/model.js';
import { loadStickerSvg, stickerViewBox } from '../content/stickers.js';
import { draw as drawTunables } from '../core/tunables.js';

/**
 * Putting her decorations onto an SVG.
 *
 * Shared deliberately between the studio and the drive scene. The studio adds marks and stickers to a
 * live document as she works; the drive scene bakes them into the car's texture. Those were separate
 * code paths at first, and the result was stickers that existed in the studio and then vanished the
 * moment she drove off - the decorations were never in the thing being baked.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
export const CLIP_ID = 'decoration-clip';
export const CRAYON_LAYER_ID = 'crayon';
/**
 * Where a mark with no panel recorded against it goes: appended at the end, over the line art, on the
 * union clip - exactly where every mark used to go.
 *
 * A separate id from `CRAYON_LAYER_ID` because they sit in different places, and one id in two
 * positions resolves to whichever was created first. That is precisely the bug this constant fixes: the
 * fallback layer was built up front, so `ensureCrayonLayerFor` found it by id and every mark stayed on
 * top of the ink.
 */
export const LEGACY_CRAYON_LAYER_ID = 'crayon-legacy';
export const STICKER_LAYER_ID = 'stickers';

/**
 * A clip path covering the union of the paintable regions.
 *
 * This is what makes generous decoration safe: whatever she puts on, only the part that lands on the
 * vehicle shows, so the baked car is always tidy and never has stickers floating in the sky beside it.
 */
export function ensureClipPath(svg: SVGSVGElement, regions: Iterable<SVGElement>): void {
  const doc = svg.ownerDocument;
  if (svg.querySelector(`#${CLIP_ID}`) !== null) return;

  const clip = doc.createElementNS(SVG_NS, 'clipPath');
  clip.setAttribute('id', CLIP_ID);
  for (const region of regions) {
    const shape = region.cloneNode(false) as SVGElement;
    // Drop the id: cloneNode copies it, and a second element with the same id is enough for a
    // #region-body selector to return this invisible clip shape instead of the one she can see.
    shape.removeAttribute('id');
    shape.removeAttribute('class');
    clip.append(shape);
  }

  const defs = doc.createElementNS(SVG_NS, 'defs');
  defs.append(clip);
  svg.append(defs);
}

/** An empty, clipped layer appended to the SVG. Created once, then reused. */
export function ensureLayer(svg: SVGSVGElement, id: string): SVGGElement {
  const existing = svg.querySelector(`#${id}`);
  if (existing !== null) return existing as SVGGElement;

  const layer = layerElement(svg.ownerDocument, id);
  layer.setAttribute('clip-path', `url(#${CLIP_ID})`);
  svg.append(layer);
  return layer;
}

function layerElement(doc: Document, id: string): SVGGElement {
  const layer = doc.createElementNS(SVG_NS, 'g') as SVGGElement;
  layer.setAttribute('id', id);
  layer.setAttribute('pointer-events', 'none');
  if (id.startsWith(CRAYON_LAYER_ID)) {
    layer.setAttribute('fill', 'none');
    layer.setAttribute('stroke-linecap', 'round');
    layer.setAttribute('stroke-linejoin', 'round');
  }
  return layer;
}

/**
 * The crayon layer for the panel a mark was drawn in - underneath that panel's line art.
 *
 * A mark used to go into one layer at the end of the document, which put it on top of every black
 * line in the picture: scribble on a wheel and the hub and the tread ticks disappeared under it. A
 * fill never had that problem, because fills live in the region group *below* the ink. Crayon on a
 * colouring page goes under the printed line, and so should this.
 *
 * Every template is built the same way - a group of paint regions, then a group of line art, at the
 * top level and again inside `#window` and each `#wheel-*`. So the position is derived rather than
 * hardcoded: insert directly after the group holding the region, which is directly before the line
 * work that belongs with it. A mark on a tyre therefore lands *inside* that wheel's group, which is
 * also what makes it turn with the wheel once baked.
 *
 * Null when the template has no such region; the caller falls back to the single top-level layer.
 */
export function ensureCrayonLayerFor(svg: SVGSVGElement, regionId: string): SVGGElement | null {
  const region = svg.querySelector(`#${regionId}`);
  const host = region?.parentElement ?? null;
  const container = host?.parentElement ?? null;
  if (host === null || container === null) return null;

  // At the top level the container is the <svg> itself, whose id is not ours to rely on.
  const isRoot = container === (svg as Element);
  if (!isRoot && container.id === '') return null;
  const id = isRoot ? CRAYON_LAYER_ID : `${CRAYON_LAYER_ID}-${container.id}`;

  const existing = svg.querySelector(`#${id}`);
  if (existing !== null) return existing as SVGGElement;

  const layer = layerElement(svg.ownerDocument, id);
  // No layer clip on these: every path carries its own region clip, which is strictly tighter, and a
  // union clip inside a wheel group is one more thing to reason about when that wheel is baked alone.
  container.insertBefore(layer, host.nextSibling);
  return layer;
}

/** Every crayon layer in the document - one per container that has marks on it. */
export function crayonLayers(svg: SVGSVGElement): SVGGElement[] {
  return [...svg.querySelectorAll(`[id^="${CRAYON_LAYER_ID}"]`)] as SVGGElement[];
}

/** The clip that confines one mark to the panel it was drawn in. */
export function regionClipId(regionId: string): string {
  return `${CLIP_ID}-${regionId}`;
}

/**
 * A clip path holding a single region's shape.
 *
 * The union clip keeps decoration on the vehicle; this one keeps a *mark* inside the panel she started
 * it in. Created on demand and once per region, because most cars only ever get marks on one or two.
 *
 * Returns false when the template has no such region - a car saved against a content pack whose panels
 * have been renamed since. The caller falls back to the union clip, so the mark still appears on the
 * bodywork rather than vanishing.
 */
export function ensureRegionClip(svg: SVGSVGElement, regionId: string): boolean {
  const doc = svg.ownerDocument;
  const clipId = regionClipId(regionId);
  if (svg.querySelector(`#${clipId}`) !== null) return true;

  const region = svg.querySelector(`#${regionId}`);
  if (region === null) return false;

  const shape = region.cloneNode(false) as SVGElement;
  // Same trap as the union clip: cloneNode copies the id, and a second element with that id makes a
  // #region-body lookup return this invisible clip shape instead of the one she can see.
  shape.removeAttribute('id');
  shape.removeAttribute('class');

  const clip = doc.createElementNS(SVG_NS, 'clipPath');
  clip.setAttribute('id', clipId);
  clip.append(shape);

  // Into the existing defs if the union clip already made one, so a car does not accumulate a defs
  // block per mark.
  const defs = svg.querySelector('defs') ?? doc.createElementNS(SVG_NS, 'defs');
  defs.append(clip);
  if (defs.parentNode === null) svg.append(defs);
  return true;
}

/**
 * One mark, clipped to the panel it was drawn in.
 *
 * Takes the `svg` rather than its document so it can build the clip itself. Splitting those apart
 * invites the bug where the attribute is set and the clip is not: a `clip-path` pointing at a
 * clipPath that is not there renders *nothing*, so the mark would silently disappear - which is the
 * opposite of the fallback this is supposed to have.
 */
export function strokePath(svg: SVGSVGElement, stroke: Stroke): SVGGElement {
  const group = svg.ownerDocument.createElementNS(SVG_NS, 'g') as SVGGElement;
  group.setAttribute('stroke', stroke.colour);

  // The clip goes on the group, not the ribs: one mark is clipped once, however many slices it is
  // drawn from. No region, or a region this template does not have, keeps the layer's union clip.
  if (stroke.regionId !== null && ensureRegionClip(svg, stroke.regionId)) {
    group.setAttribute('clip-path', `url(#${regionClipId(stroke.regionId)})`);
  }

  drawRibs(group, stroke);
  return group;
}

/**
 * Fill a mark's group with its ribs, replacing whatever was there.
 *
 * Separate from `strokePath` because the studio rebuilds the mark on every `pointermove` as she draws,
 * and rebuilding it must not touch the group's clip - which is the thing that keeps the mark inside its
 * panel while she is still drawing it.
 */
export function drawRibs(group: SVGGElement, stroke: Stroke): void {
  const doc = group.ownerDocument;
  const ribs = strokeRibs(stroke, drawTunables.strokeWidthJitter);
  const paths = ribs.map((rib) => {
    const path = doc.createElementNS(SVG_NS, 'path') as SVGPathElement;
    path.setAttribute('d', rib.d);
    path.setAttribute('stroke-width', String(rib.width));
    return path;
  });
  group.replaceChildren(...paths);
}

/**
 * A sticker as a positioned group: centred on the point she tapped, tilted about that same point, and
 * scaled relative to the template's width so it stays the same size on the car whatever the art scale.
 */
export function stickerGroup(
  doc: Document,
  sticker: PlacedSticker,
  source: SVGSVGElement,
  templateWidth: number,
): SVGGElement {
  const { width, height } = stickerViewBox(source);
  const scale = (sticker.scale * templateWidth) / width;

  const group = doc.createElementNS(SVG_NS, 'g') as SVGGElement;
  group.setAttribute(
    'transform',
    `translate(${round(sticker.x)} ${round(sticker.y)}) rotate(${round(
      (sticker.rotation * 180) / Math.PI,
    )}) scale(${scale.toFixed(4)}) translate(${-width / 2} ${-height / 2})`,
  );
  for (const child of source.children) group.append(child.cloneNode(true));
  return group;
}

export interface Decorations {
  readonly colours: Readonly<Record<string, string>>;
  readonly strokes: readonly Stroke[];
  readonly stickers: readonly PlacedSticker[];
}

export interface WheelTarget {
  readonly groupId: string;
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
}

/**
 * Which wheel a sticker belongs to, if any.
 *
 * A sticker that lands on a wheel is baked *into that wheel's* texture rather than the body's, so it
 * turns with the wheel as she drives - which is far more fun than a star sitting frozen on a spinning
 * tyre. Generous radius: she is aiming with a finger, and a sticker slightly overlapping the tyre
 * clearly belongs to it.
 */
export function wheelForSticker(
  sticker: PlacedSticker,
  wheels: readonly WheelTarget[],
): WheelTarget | null {
  let best: WheelTarget | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const wheel of wheels) {
    const distance = Math.hypot(sticker.x - wheel.cx, sticker.y - wheel.cy);
    if (distance <= wheel.r && distance < bestDistance) {
      best = wheel;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * Apply a whole saved decoration set to an SVG in one pass. Used by the drive scene before baking the
 * car's texture, so what she drives is what she decorated.
 */
export async function applyDecorations(
  svg: SVGSVGElement,
  decorations: Decorations,
  templateWidth: number,
  loadSticker: (id: string) => Promise<SVGSVGElement | null> = loadStickerSvg,
  wheels: readonly WheelTarget[] = [],
): Promise<void> {
  const regions = [...svg.querySelectorAll('[id^="region-"]')] as SVGElement[];

  for (const region of regions) {
    const colour = decorations.colours[region.id];
    if (colour !== undefined) region.setAttribute('fill', colour);
  }

  if (decorations.strokes.length === 0 && decorations.stickers.length === 0) return;

  ensureClipPath(svg, regions);

  for (const stroke of decorations.strokes) {
    // Each mark goes under the line art of the panel it was drawn in. A mark with no panel - saved
    // before panels were tracked - keeps the single top-level layer it was drawn against.
    const layer =
      (stroke.regionId === null ? null : ensureCrayonLayerFor(svg, stroke.regionId)) ??
      ensureLayer(svg, LEGACY_CRAYON_LAYER_ID);
    layer.append(strokePath(svg, stroke));
  }

  if (decorations.stickers.length > 0) {
    const bodyLayer = ensureLayer(svg, STICKER_LAYER_ID);

    for (const sticker of decorations.stickers) {
      const source = await loadSticker(sticker.id);
      if (source === null) continue;

      const group = stickerGroup(svg.ownerDocument, sticker, source, templateWidth);
      const wheel = wheelForSticker(sticker, wheels);

      if (wheel === null) {
        bodyLayer.append(group);
        continue;
      }

      // Into the wheel's own group, so it is picked up when that wheel is baked to its own texture -
      // and therefore turns with it. Clipped to the wheel's shapes so it cannot spill onto the body.
      const host = svg.querySelector(`#${wheel.groupId}`);
      if (host === null) {
        bodyLayer.append(group);
        continue;
      }
      const wheelLayer = ensureWheelLayer(svg, host, wheel.groupId);
      wheelLayer.append(group);
    }
  }
}

/** A clipped sticker layer inside a wheel group, created once per wheel. */
function ensureWheelLayer(svg: SVGSVGElement, host: Element, groupId: string): SVGGElement {
  const id = `${STICKER_LAYER_ID}-${groupId}`;
  const existing = svg.querySelector(`#${id}`);
  if (existing !== null) return existing as SVGGElement;

  const doc = svg.ownerDocument;
  const clipId = `${CLIP_ID}-${groupId}`;

  if (svg.querySelector(`#${clipId}`) === null) {
    const clip = doc.createElementNS(SVG_NS, 'clipPath');
    clip.setAttribute('id', clipId);
    // Only this wheel's own shapes, so a sticker on the tyre stays on the tyre.
    for (const region of host.querySelectorAll('[id^="region-"]')) {
      const shape = region.cloneNode(false) as SVGElement;
      shape.removeAttribute('id');
      shape.removeAttribute('class');
      clip.append(shape);
    }
    const defs = doc.createElementNS(SVG_NS, 'defs');
    defs.append(clip);
    svg.append(defs);
  }

  const layer = doc.createElementNS(SVG_NS, 'g') as SVGGElement;
  layer.setAttribute('id', id);
  layer.setAttribute('clip-path', `url(#${clipId})`);
  layer.setAttribute('pointer-events', 'none');
  host.append(layer);
  return layer;
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
